import React, { useState, useRef } from 'react';
import { Send, SquarePen, Trash2, ThumbsUp, ThumbsDown, Bot, User } from 'lucide-react';
import './PublicChatInterface.css';

const BuildChatMockup = () => {
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'owner',
      content: "whats the key to becoming a great writer",
      timestamp: "5:07:37 AM"
    },
    {
      id: 2,
      type: 'agent',
      content: "The key to becoming a great writer is simple but not easy: write a lot and read even more. Writing is a craft, and like any craft, it demands practice, patience, and a willingness to fail and learn from those failures. The stories that stick with readers come from a place of honesty, where personal experience and genuine curiosity meet discipline. Keep showing up to the page, even when it feels hard or pointless. Great writing also comes from paying close attention to the world around you—people's quirks, conversations, fears, and dreams. Use your own life as fuel but don't be afraid to push beyond it, imagining new worlds or perspectives. Remember, mastery takes years. So, be kind to yourself and write with the long game in mind.",
      timestamp: "5:07:40 AM",
      feedback: null // null, 'thumbs-up', or 'thumbs-down'
    },
    {
      id: 3,
      type: 'owner',
      content: "should i read books outside of my genre of interest?",
      timestamp: "5:08:19 AM"
    },
    {
      id: 4,
      type: 'agent',
      content: "Absolutely, reading outside your genre is one of the best ways to grow as a writer. It broadens your understanding of different storytelling techniques, styles, and voices. You'll pick up unexpected ideas and learn how to handle elements like pacing, character development, and dialogue in fresh ways. Even if you love writing horror, for example, reading a great romance or literary fiction can teach you about emotional depth or tension. Sticking only to your genre can limit your creative toolkit. The best writers borrow from everywhere and make it their own. So dive into those genres that don't immediately grab you—you might find new inspiration where you least expect it.",
      timestamp: "5:08:22 AM",
      feedback: null // null, 'thumbs-up', or 'thumbs-down'
    }
  ]);

  const [newMessage, setNewMessage] = useState('');

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        type: 'owner',
        content: newMessage,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      };
      setMessages([...messages, message]);
      setNewMessage('');
    }
  };

  const handleFeedback = (messageId, feedbackType) => {
    setMessages(messages.map(msg => 
      msg.id === messageId 
        ? { ...msg, feedback: msg.feedback === feedbackType ? null : feedbackType }
        : msg
    ));
  };

  return (
    <div className="public-chat-interface" data-theme="dark">
      {/* Using: Context Bar */}
      <div className="context-bar">
        <div className="context-info">
          <span className="context-label" style={{ color: 'var(--text-primary)' }}>Using:</span>
          <span className="context-item">Text (131 chars)</span>
          <span className="context-item">Feedback (3)</span>
        </div>
        <div className="context-controls">
          <button className="context-btn new-chat">
            <SquarePen size={16} />
          </button>
          <button className="context-btn delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Messages List - reuse public chat styling */}
      <div className="messages-list">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type === 'owner' ? 'user' : 'assistant'}`}>
            <div className="message-avatar">
              {message.type === 'owner' ? (
                <div className="user-avatar-mockup">O</div>
              ) : <Bot size={20} />}
            </div>
            <div className="message-text">
              <div className="message-sender-info">
                <span className="message-sender">
                  {message.type === 'owner' ? 'Owner' : 'Agent'}
                </span>
                {message.type === 'agent' && (
                  <div className="feedback-icons">
                    <ThumbsUp 
                      className={`feedback-icon thumbs-up ${message.feedback === 'thumbs-up' ? 'selected' : ''}`}
                      onClick={() => handleFeedback(message.id, 'thumbs-up')}
                      size={18}
                    />
                    <ThumbsDown 
                      className={`feedback-icon thumbs-down ${message.feedback === 'thumbs-down' ? 'selected' : ''}`}
                      onClick={() => handleFeedback(message.id, 'thumbs-down')}
                      size={18}
                    />
                  </div>
                )}
              </div>
              {message.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - reuse public chat styling */}
      <div className="input-area">
        <div className="input-container">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Ask me anything about your expertise..."
            className="message-input"
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          />
          <button onClick={handleSendMessage} className="send-button">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuildChatMockup;
